import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription, // Add DialogDescription
  DialogFixedFooter,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { Button, ButtonProps } from "components/button";
import { Icon, IconProps } from "components/icon/Icon";
import { ChevronDown } from "lucide-react";
import { FileUploadDropZone } from "./FileUploadDropZone";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useFileUpload } from "@/contexts/FileUploadContext";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";

const DEFAULT_LANGUAGE = "en";
const DEFAULT_WORD_COUNT = 2500;

interface CreateSummaryDialogProps {
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  triggerButton?: ButtonProps;
  icon?: IconProps["name"];
  accept?: Record<string, string[]>;
  multiple?: boolean;
  maxSizeMB?: number;
  error?: string;
  defaultWordCount?: number;
  defaultLanguage?: string;
}

export const CreateSummaryDialog = ({
  open,
  onOpenChange,
  triggerButton,
  accept,
  multiple = false,
  maxSizeMB,
  error,
  defaultWordCount,
  defaultLanguage,
}: CreateSummaryDialogProps) => {
  const { t } = useTranslation(["general"]);
  const { uploadFile, file, isUploading } = useFileUpload();
  const { features } = useFeatureFlags();

  const OUTPUT_LANGUAGE_OPTIONS: Record<string, string> = {
    en: t("home:fileUpload.outputLanguage.en"),
    de: t("home:fileUpload.outputLanguage.de"),
  };

  const DROPDOWN_MENU_ITEMS = [
    {
      label: t("home:fileUpload.desiredWordCount.shortSummaryLabel"),
      pages: t("home:fileUpload.desiredWordCount.shortSummaryPages"),
      wordCount: 1800,
    },
    {
      label: t("home:fileUpload.desiredWordCount.mediumSummaryLabel"),
      pages: t("home:fileUpload.desiredWordCount.mediumSummaryPages"),
      wordCount: 2500,
    },
    {
      label: t("home:fileUpload.desiredWordCount.longSummaryLabel"),
      pages: t("home:fileUpload.desiredWordCount.longSummaryPages"),
      wordCount: 3500,
    },
  ];

  const [selectedWordCount, setSelectedWordCount] = React.useState(
    defaultWordCount ?? DEFAULT_WORD_COUNT,
  );
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(
    defaultLanguage ?? DEFAULT_LANGUAGE,
  );
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] =
    React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setSelectedWordCount(defaultWordCount ?? DEFAULT_WORD_COUNT);
    setSelectedLanguage(defaultLanguage ?? DEFAULT_LANGUAGE);
  }, [open, defaultWordCount, defaultLanguage]);

  const selectedItem =
    DROPDOWN_MENU_ITEMS.find((item) => item.wordCount === selectedWordCount) ??
    DROPDOWN_MENU_ITEMS[1];

  const handleSelectItem = (item: (typeof DROPDOWN_MENU_ITEMS)[number]) => {
    setSelectedWordCount(item.wordCount);
    setIsDropdownOpen(false);
  };

  const handleSelectLanguage = (code: string) => {
    setSelectedLanguage(code);
    setIsLanguageDropdownOpen(false);
  };

  const handleClickCreate = () => {
    uploadFile(selectedWordCount, selectedLanguage);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {triggerButton && (
        <DialogTrigger asChild>
          <Button {...triggerButton} />
        </DialogTrigger>
      )}
      <DialogContent className="overflow-visible">
        <DialogTitle className="hidden">File Upload</DialogTitle>
        <DialogDescription className="hidden">
          Upload a file and select the desired summary length.
        </DialogDescription>
        <div className="grid">
          <FileUploadDropZone
            accept={accept}
            multiple={multiple}
            maxSizeMB={maxSizeMB}
            error={error}
          />
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground px-[0.875rem] mt-5">
              {t("home:fileUpload.desiredWordCount.title")}
            </DropdownMenuLabel>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex justify-between space-x-2 px-[0.875rem]"
                testId="summary-length-trigger"
              >
                <div>
                  <span className="mr-[0.5rem] text-sm leading-5 text-foreground">
                    {selectedItem.label}
                  </span>
                  <span className="text-sm text-muted-foreground leading-5 font-light">
                    {selectedItem.pages}
                  </span>
                </div>
                <ChevronDown className="h-[1rem] w-[1rem] text-sidebar-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sizeToTrigger
              align="start"
              className="mt-[0rem]"
              forceMount
            >
              {DROPDOWN_MENU_ITEMS.map((item) => (
                <Button
                  key={item.label}
                  variant="ghost"
                  className="flex justify-between space-x-2 px-[0.875rem] w-full"
                  onClick={() => handleSelectItem(item)}
                  testId={`summary-length-option-${item.wordCount}`}
                >
                  <div>
                    <span className="mr-[0.5rem] text-sm leading-5 text-foreground font-light">
                      {item.label}
                    </span>
                    <span className="text-sm text-muted-foreground leading-5 font-light">
                      {item.pages}
                    </span>
                  </div>
                </Button>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {features.isMultilingualSupportEnabled && (
            <DropdownMenu
              open={isLanguageDropdownOpen}
              onOpenChange={setIsLanguageDropdownOpen}
            >
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground px-[0.875rem] mt-3">
                {t("home:fileUpload.outputLanguage.title")}
              </DropdownMenuLabel>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex justify-between space-x-2 px-[0.875rem]"
                >
                  <span className="text-sm leading-5 text-foreground">
                    {OUTPUT_LANGUAGE_OPTIONS[selectedLanguage]}
                  </span>
                  <ChevronDown className="h-[1rem] w-[1rem] text-sidebar-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                sizeToTrigger
                align="start"
                className="mt-[0rem]"
                forceMount
              >
                {Object.entries(OUTPUT_LANGUAGE_OPTIONS).map(
                  ([code, label]) => (
                    <Button
                      key={code}
                      variant="ghost"
                      className="flex justify-between space-x-2 px-[0.875rem] w-full"
                      onClick={() => handleSelectLanguage(code)}
                    >
                      <span className="text-sm leading-5 text-foreground font-light">
                        {label}
                      </span>
                    </Button>
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <DialogFixedFooter className="flex justify-between p-3 pt-7 px-[0.625rem] items-center">
          <div className="flex justify-end ml-auto">
            <DialogClose asChild>
              <Button
                variant="default"
                icon={
                  <Icon
                    name="sparkles"
                    className="foreground h-[1rem] w-[1rem]"
                  />
                }
                onClick={handleClickCreate}
                disabled={!file || isUploading}
                testId="file-upload-cta"
              >
                {t("home:button.mainCtaLabel")}
              </Button>
            </DialogClose>
          </div>
        </DialogFixedFooter>
      </DialogContent>
    </Dialog>
  );
};
